import React, { ComponentType } from 'react';
import {
  Box,
  Text,
  SimpleGrid,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import AutoRadioInput from 'components/fields/auto/RadioInput';
import AutoSelectInput from 'components/fields/auto/SelectInput';
import SelectSearch from 'components/fields/SelectSearch';
import AutoFormInput from 'components/fields/auto/FormInput';
import { FILE_TYPE } from 'variables/constants';
import { PROVINCES } from 'variables/options';
import getRadioYesNo from 'helpers/getRadioYesNo';

interface AgencyBillingData {
  financing_type?: string;
  is_signed_contract_attached?: boolean;
  province_code?: string;
  business_code?: string;
  total_premium?: string;
  service_fee?: string;
}

interface AgencyBillingFieldsProps {
  isDisabled?: boolean;
  agencyBillingData?: AgencyBillingData;
  sectionKey: number;
  onFieldChange: (name: string, value: any) => void;
  formType: 'auto' | 'home';
  components?: {
    FormInput: ComponentType<any>;
    SelectInput: ComponentType<any>;
    RadioInput: ComponentType<any>;
  };
}

// Business code options
const AUTO_BUSINESS_CODE_OPTIONS = [{ value: 'AUTO', label: 'Auto' }];
const HOME_BUSINESS_CODE_OPTIONS = [{ value: 'PROPERTY', label: 'Property' }];

const AgencyBillingFields: React.FC<AgencyBillingFieldsProps> = ({
  isDisabled,
  agencyBillingData,
  sectionKey,
  onFieldChange,
  formType,
  components,
}) => {
  const FormInput = components?.FormInput ?? AutoFormInput;
  const SelectInput = components?.SelectInput ?? AutoSelectInput;
  const RadioInput = components?.RadioInput ?? AutoRadioInput;

  const businessCodeOptions =
    formType === 'auto'
      ? AUTO_BUSINESS_CODE_OPTIONS
      : HOME_BUSINESS_CODE_OPTIONS;
  const defaultBusinessCode = formType === 'auto' ? 'AUTO' : 'PROPERTY';
  const handleFinancingChange = (value: string) => {
    if (agencyBillingData?.financing_type !== value) {
      onFieldChange('financing_type', value);
    }
  };

  const handleContractAttachmentChange = (value: string) => {
    const boolValue = value === 'true';
    if (agencyBillingData?.is_signed_contract_attached !== boolValue) {
      onFieldChange('is_signed_contract_attached', boolValue);
    }
  };

  const handleProvinceChange = (value: string | number | null) => {
    if (agencyBillingData?.province_code !== value) {
      onFieldChange('province_code', value as string);
    }
  };

  const handleBusinessCodeChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const { value } = event.target;
    if (agencyBillingData?.business_code !== value) {
      onFieldChange('business_code', value);
    }
  };

  const handleTotalPremiumChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;
    if (agencyBillingData?.total_premium !== value) {
      onFieldChange('total_premium', value);
    }
  };

  const handleServiceFeeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;
    if (agencyBillingData?.service_fee !== value) {
      onFieldChange('service_fee', value);
    }
  };

  // Calculate Total Invoiced To Customer
  const totalInvoicedToCustomer = () => {
    const totalPremium = parseFloat(agencyBillingData?.total_premium || '0');
    const serviceFee = parseFloat(agencyBillingData?.service_fee || '0');
    return (totalPremium + serviceFee).toFixed(2);
  };

  const showContractAttachment =
    agencyBillingData?.financing_type === 'premium_financing';

  return (
    <Box mt={{ base: '15px', md: '20px' }} px={{ base: '10px', md: '25px' }}>
      <Text
        fontWeight="bold"
        fontSize={{ base: 'lg', md: 'xl' }}
        mb={{ base: '15px', md: '20px' }}
      >
        Agency Billing Information
      </Text>

      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 3, xl: 4 }}
        spacing={{ base: '15px', md: '20px' }}
        mb={{ base: '15px', md: '20px' }}
      >
        <RadioInput
          section={sectionKey}
          label="Financing"
          options={[
            { value: 'paid_full', label: 'Paid Full' },
            { value: 'premium_financing', label: 'Premium Financing' },
          ]}
          value={agencyBillingData?.financing_type || ''}
          onChange={handleFinancingChange}
          required
          stacked
          disabled={isDisabled}
        />

        {showContractAttachment && (
          <RadioInput
            section={sectionKey}
            label="Signed contract attached"
            options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
            value={getRadioYesNo(
              agencyBillingData?.is_signed_contract_attached,
            )}
            onChange={handleContractAttachmentChange}
            message={{
              validationId: 'facility_refusal_letter',
              storageKey: 'agency_billing',
              index: 1,
              file: FILE_TYPE.TYPE_FACILITY_REFUSAL_LETTER_FOR_AUTO,
              manual: {
                message: 'Please upload Facility Refusal Letter',
                show: agencyBillingData?.is_signed_contract_attached === true,
              },
              hideRemarks: true,
              compact: true,
            }}
            required
            stacked
            disabled={isDisabled}
          />
        )}
      </SimpleGrid>

      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 3, xl: 4 }}
        spacing={{ base: '15px', md: '20px' }}
      >
        <FormControl isRequired>
          <FormLabel ml="0">Province</FormLabel>
          <SelectSearch
            name="province_code"
            value={agencyBillingData?.province_code || 'ON'}
            onChange={handleProvinceChange}
            options={PROVINCES}
            placeholder="Select province"
            isDisabled={isDisabled}
          />
        </FormControl>

        <SelectInput
          section={sectionKey}
          label="Business Code"
          options={businessCodeOptions}
          value={agencyBillingData?.business_code || defaultBusinessCode}
          onChange={handleBusinessCodeChange}
          placeholder="Select business code"
          required
          disabled={isDisabled}
        />

        <FormInput
          section={sectionKey}
          label="Total Premium"
          name="total_premium"
          value={agencyBillingData?.total_premium || ''}
          onChange={handleTotalPremiumChange}
          type="number"
          step="0.01"
          required
          disabled={isDisabled}
        />

        <FormInput
          section={sectionKey}
          label="Service Fee"
          name="service_fee"
          value={agencyBillingData?.service_fee || ''}
          onChange={handleServiceFeeChange}
          type="number"
          step="0.01"
          required
          disabled={isDisabled}
        />

        <FormControl>
          <FormLabel ml="0">Total Invoiced To Customer</FormLabel>
          <Box
            px="12px"
            py="8px"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="2xl"
            bg="gray.50"
            minH="40px"
            display="flex"
            alignItems="center"
            color="gray.700"
            fontSize="sm"
            fontWeight="medium"
          >
            {totalInvoicedToCustomer()}
          </Box>
        </FormControl>
      </SimpleGrid>
    </Box>
  );
};

export default AgencyBillingFields;
